import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { supabase, isAuthConfigured } from '../services/supabase';
import { getNlSearchPref, setNlSearchPref, getIntelCohort } from '../services/api';
import { slugifyTitle } from '../services/titleLinks';
import { trackEvent } from '../services/analytics';
import { hasIndustryAccess } from '../services/industryAccess';

/**
 * Logged-in account surface. Two things live here today:
 *   1. The "Smart search" toggle — flips the conversational LLM query-understanding
 *      layer on/off for THIS browser so you can A/B the difference yourself.
 *   2. A peek at the Content Intelligence viewer (the dark-launch cohort) — the
 *      per-title evidence pages that exist but aren't linked anywhere public yet.
 *
 * Auth-gated: unauthenticated visitors are bounced to the homepage.
 */
const Toggle = ({ on, onChange, id }) => (
  <button
    id={id}
    role="switch"
    aria-checked={on}
    onClick={() => onChange(!on)}
    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-binge-gold/60 ${
      on ? 'bg-binge-gold' : 'bg-white/15'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-binge-bg shadow transition-transform ${
        on ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const ProfilePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined); // undefined = still resolving
  const [nlOn, setNlOn] = useState(getNlSearchPref());
  const [cohort, setCohort] = useState([]);
  const industryAccess = hasIndustryAccess(user);

  // Resolve session; redirect out if not logged in (once auth is configured).
  useEffect(() => {
    if (!isAuthConfigured) { setUser(null); return; }
    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (alive) setUser(data.session?.user ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (alive) setUser(session?.user ?? null);
    });
    return () => { alive = false; sub?.subscription?.unsubscribe?.(); };
  }, []);

  useEffect(() => {
    if (user === null) navigate('/', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    getIntelCohort().then((t) => setCohort(t || []));
  }, []);

  const flipNl = (on) => {
    setNlOn(on);
    setNlSearchPref(on);
    trackEvent('Smart Search Toggled', { enabled: on });
  };

  const signOut = async () => {
    if (supabase) await supabase.auth.signOut();
    trackEvent('Logged Out');
    navigate('/', { replace: true });
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-binge-bg text-binge-ink flex items-center justify-center">
        <span className="text-binge-sub">Loading your profile…</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-binge-bg text-binge-ink flex flex-col">
      <header className="flex items-center justify-between px-5 sm:px-8 py-4 border-b border-white/10">
        <Link to="/" className="font-serif text-lg tracking-tight">What to Binge</Link>
        <button onClick={signOut} className="text-sm text-binge-sub hover:text-binge-ink transition-colors">
          Log out
        </button>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-5 sm:px-8 py-10">
        <p className="text-xs uppercase tracking-[0.2em] text-binge-faint">Account</p>
        <h1 className="font-serif text-3xl mt-1 mb-1">Your settings</h1>
        {user?.email && <p className="text-sm text-binge-sub mb-9">{user.email}</p>}
        {industryAccess && <div className="rounded-xl border border-binge-gold/40 bg-binge-gold/10 px-4 py-3 mb-6"><strong className="text-binge-gold">Industry preview access enabled</strong><p className="text-sm text-binge-sub mt-1">Your internal account can inspect the complete 50-title intelligence cohort and its Industry Lens. Platform-specific strategic fit remains intentionally unavailable until a real company profile exists.</p><Link to="/industry" className="inline-block mt-3 text-sm font-semibold text-binge-gold hover:text-binge-cream">Open Industry Intelligence →</Link></div>}

        {/* ---- Smart search toggle ---- */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6 mb-6">
          <div className="flex items-start justify-between gap-5">
            <div>
              <h2 className="font-serif text-xl">Smart search <span className="text-binge-gold text-sm align-middle">beta</span></h2>
              <p className="text-sm text-binge-sub mt-2 leading-relaxed">
                Understands conversational queries — “having lunch, show me 30-minute
                animal documentaries on Netflix” — by reading intent and extracting
                filters before searching. When off, search uses the fast keyword parser
                only. Simple and repeated queries stay instant either way.
              </p>
            </div>
            <Toggle id="nl-search" on={nlOn} onChange={flipNl} />
          </div>
          <p className="text-xs text-binge-faint mt-4">
            {nlOn
              ? 'On — conversational queries get parsed by AI. Toggle off to feel the plain search.'
              : 'Off — plain keyword search. Toggle on to try the conversational understanding.'}
            {' '}Applies to this browser and takes effect on your next search.
          </p>
        </section>

        {/* ---- Content Intelligence peek ---- */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6">
          <h2 className="font-serif text-xl">Content Intelligence <span className="text-binge-gold text-sm align-middle">preview</span></h2>
          <p className="text-sm text-binge-sub mt-2 leading-relaxed">
            The per-title evidence view — content DNA, audience fit, source-aware
            availability and explained similar titles. Built for a verified cohort;
            open one to see what’s behind the black box.
          </p>
          {cohort.length ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {(industryAccess ? cohort : cohort.slice(0, 8)).map((t) => (
                <Link
                  key={`${t.media_type}-${t.tmdb_id}`}
                  to={`/title/${t.media_type}/${t.tmdb_id}/${slugifyTitle(t.title)}`}
                  onClick={() => trackEvent('intel_preview_opened', { title: t.title, from: 'profile' })}
                  className="text-sm rounded-full border border-white/15 px-3 py-1.5 text-binge-cream hover:border-binge-gold hover:text-binge-gold transition-colors"
                >
                  {t.title}
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-xs text-binge-faint mt-4">Preview cohort unavailable right now.</p>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProfilePage;
